/**
 * Schedule Occurrence Utilities
 * Builds schedule occurrences from lessons, terms, and overrides for academy owner and coach schedule views.
 */

import { format, parse as parseDateFns, startOfDay } from 'date-fns';
import {
  generateLessonOccurrences,
  generatePrivateLessonOccurrenceDates,
  resolveSessionCoachIds,
  resolveSessionCourtIds,
  type Lesson,
  type LessonOverride,
  type LessonSession,
  type OwnerScheduleEnrollmentBooking,
  type OwnerSchedulePendingBooking,
  type RemovedFromSessionEnrollmentRef,
  type ReportedAbsenceSessionRef,
  type Term,
  type CourtHireBooking,
  type PlayEvent,
} from '@court-sync/shared';
import { courtNamesLabelFromCourtIds, courtNamesLabelFromLesson } from './courtDisplay';
import type { UnifiedOccupancyCalendarItem } from '@/services/occupancy';

export type MakeupSessionRef = { lessonId: string; date: string };

export type ScheduleOccurrenceSourceType = 'lesson' | 'court_hire' | 'closure' | 'play_event';

const LESSON_OCCURRENCE_SOURCE = { sourceType: 'lesson' as const };

export type { ReportedAbsenceSessionRef };

/** Parse YYYY-MM-DD as local calendar date (avoids UTC shift). */
function parseLocalYmd(ymd: string): Date {
  const d = parseDateFns(ymd.replace(/T.*/, '').slice(0, 10), 'yyyy-MM-dd', new Date());
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Pending bookings only (for schedule badges). Session date YYYY-MM-DD when session-scoped. */
export type SchedulePendingBookingRef = OwnerSchedulePendingBooking;

/** Confirmed or pending roster booking (for per-session enrollment counts on calendar chips). */
export type ScheduleEnrollmentBookingRef = OwnerScheduleEnrollmentBooking;

/** Student removed from a single session (attendance status cancelled). */
export type RemovedFromSessionRef = RemovedFromSessionEnrollmentRef;

/**
 * Count enrolled students for one lesson occurrence (matches session roster rules).
 * Term-wide bookings (no sessionDate) count on every occurrence; session bookings only on matching date.
 */
export function countEnrollmentForOccurrence(
  lessonId: string,
  dateKey: string,
  enrollmentBookings: ScheduleEnrollmentBookingRef[],
  removedFromSession: RemovedFromSessionRef[] = []
): number {
  const removedIds = new Set(
    removedFromSession
      .filter((r) => r.lessonId === lessonId && r.date.replace(/T.*/, '').slice(0, 10) === dateKey)
      .map((r) => r.bookingId)
  );
  let count = 0;
  for (const b of enrollmentBookings) {
    if (b.lessonId !== lessonId) continue;
    if (removedIds.has(b.bookingId)) continue;
    const sd = b.sessionDate?.replace(/T.*/, '').slice(0, 10);
    if (!sd) {
      count += 1;
    } else if (sd === dateKey) {
      count += 1;
    }
  }
  return count;
}

/**
 * Count pending bookings for one lesson occurrence (term-wide pending count for every week; session pending only on matching date).
 */
export function countPendingForOccurrence(
  lessonId: string,
  dateKey: string,
  pendingBookings: SchedulePendingBookingRef[]
): number {
  let count = 0;
  for (const b of pendingBookings) {
    if (b.lessonId !== lessonId) continue;
    if (!b.sessionDate) {
      count += 1;
    } else if (b.sessionDate.split('T')[0] === dateKey) {
      count += 1;
    }
  }
  return count;
}

/**
 * Roster size for one calendar occurrence: term enrollments + session drop-ins when booking list
 * is available; falls back to lesson.currentEnrollment when the list was not loaded.
 */
export function resolveOccurrenceEnrollmentCount(
  lesson: Pick<Lesson, 'id' | 'currentEnrollment'>,
  dateKey: string,
  scheduleEnrollmentBookings: ScheduleEnrollmentBookingRef[] | undefined,
  removedFromSession: RemovedFromSessionRef[] = []
): number {
  if (scheduleEnrollmentBookings !== undefined) {
    return countEnrollmentForOccurrence(
      lesson.id,
      dateKey,
      scheduleEnrollmentBookings,
      removedFromSession
    );
  }
  return lesson.currentEnrollment ?? 0;
}

/** True for private lessons not linked to a program/term (standalone package). */
export function isStandalonePrivateLesson(
  lesson: Pick<Lesson, 'lessonType' | 'termId'>
): boolean {
  return lesson.lessonType === 'private' && !lesson.termId;
}

/** Session row cancelled, or on/after lesson-wide cancel-from date (package/term). */
export function isLessonOccurrenceCancelledOnDate(
  lesson: Pick<Lesson, 'cancelledFromDate'>,
  sessionDate: Date,
  sessionStatus: 'scheduled' | 'cancelled' | 'postponed'
): boolean {
  if (sessionStatus === 'cancelled') return true;
  const raw = lesson.cancelledFromDate;
  if (!raw) return false;
  const fromYmd =
    typeof raw === 'string'
      ? raw.replace(/T.*/, '').slice(0, 10)
      : format(new Date(raw), 'yyyy-MM-dd');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromYmd)) return false;
  const occYmd = format(sessionDate, 'yyyy-MM-dd');
  return occYmd >= fromYmd;
}

function privateOccurrenceFlags(lesson: Lesson): {
  isPrivate: true;
  isStandalonePrivate: boolean;
} {
  return {
    isPrivate: true,
    isStandalonePrivate: isStandalonePrivateLesson(lesson),
  };
}

export interface ScheduleOccurrence {
  /** Discriminator for unified club/academy schedule views. */
  sourceType: ScheduleOccurrenceSourceType;
  date: Date;
  lessonId: string;
  lesson: Lesson;
  lessonName: string;
  lessonType: 'group' | 'private' | 'camp';
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'cancelled' | 'postponed';
  ballColor?: string;
  ageGroup?: string;
  skillLevel?: string;
  enrollmentCount: number;
  maxCapacity: number;
  isPrivate?: boolean;
  /** Private lesson with explicit package dates (no term); distinct calendar styling from term-bound 1-on-1 */
  isStandalonePrivate?: boolean;
  /** Camp lesson (standalone; styling) */
  isCamp?: boolean;
  termName?: string;
  /** Students attending this occurrence via a make-up credit (session booking) */
  makeupCount?: number;
  /** Pending confirmation/payment for this occurrence (when pending bookings list is supplied) */
  pendingEnrollmentCount?: number;
  /** Students with reported absence or missed attendance for this session */
  reportedAbsenceCount?: number;
  /** Materialized `lesson_sessions` row when the backend has synced this occurrence */
  sessionId?: string;
  /** Resolved coach IDs for this occurrence when returned from lesson-sessions API */
  sessionCoachIds?: string[];
  hasCoachOverride?: boolean;
  coachOverrideCreatedAt?: Date;
  coachOverrideCreatedBy?: string;
  /** Comma-separated court names when available */
  courtLabel?: string;
  /** Resolved court IDs for this occurrence when returned from lesson-sessions API */
  sessionCourtIds?: string[];
  resolvedCourtIds?: string[];
  hasCourtOverride?: boolean;
}

function normalizeSessionTimeHHmm(t: string | undefined): string | undefined {
  if (!t) return undefined;
  const s = t.trim();
  if (s.length >= 5) return s.slice(0, 5);
  return s;
}

function courtFieldsForOccurrence(
  lesson: Lesson,
  session: LessonSession | undefined,
  courtNamesById?: Record<string, string>
): Pick<
  ScheduleOccurrence,
  'sessionCourtIds' | 'resolvedCourtIds' | 'hasCourtOverride' | 'courtLabel'
> {
  const resolvedCourtIds = resolveSessionCourtIds(lesson, session);
  return {
    sessionCourtIds: session?.courtIds,
    resolvedCourtIds,
    hasCourtOverride: session?.hasCourtOverride,
    courtLabel: courtNamesLabelFromCourtIds(resolvedCourtIds, courtNamesById),
  };
}

/** Context to add DB-only sessions (e.g. replacement after postpone to a non-pattern day). */
export type MergeMaterializedSessionsContext = {
  lessons: Lesson[];
  terms: Term[];
  makeupSessions?: MakeupSessionRef[];
  pendingScheduleBookings?: SchedulePendingBookingRef[];
  scheduleEnrollmentBookings?: ScheduleEnrollmentBookingRef[];
  removedFromSessionEnrollments?: RemovedFromSessionRef[];
  reportedAbsenceSessions?: ReportedAbsenceSessionRef[];
  courtNamesById?: Record<string, string>;
};

/**
 * Attach `sessionId` and authoritative times/status from materialized lesson_sessions rows
 * where `lessonId` + calendar date match a generated occurrence.
 * When `mergeContext` is set, also appends sessions that exist in DB but have no generated
 * occurrence (e.g. group lesson moved from Wed to Thu).
 */
export function mergeMaterializedSessionsIntoOccurrences(
  occurrences: ScheduleOccurrence[],
  lessonSessions: LessonSession[] | undefined,
  mergeContext?: MergeMaterializedSessionsContext
): ScheduleOccurrence[] {
  if (!lessonSessions?.length) {
    if (!mergeContext?.courtNamesById) return occurrences;
    return occurrences.map((o) => ({
      ...o,
      courtLabel: courtNamesLabelFromLesson(o.lesson, mergeContext.courtNamesById),
    }));
  }
  const byLessonDate = new Map<string, LessonSession>();
  for (const s of lessonSessions) {
    const ymd = s.sessionDate.replace(/T.*/, '').slice(0, 10);
    const key = `${s.lessonId}:${ymd}`;
    if (!byLessonDate.has(key)) byLessonDate.set(key, s);
  }
  const enriched = occurrences.map((occ) => {
    const dateKey = format(occ.date, 'yyyy-MM-dd');
    const sess = byLessonDate.get(`${occ.lessonId}:${dateKey}`);
    const courtFields = courtFieldsForOccurrence(occ.lesson, sess, mergeContext?.courtNamesById);
    if (!sess) return { ...occ, ...courtFields };
    const st = normalizeSessionTimeHHmm(sess.startTime) ?? occ.startTime;
    const et = normalizeSessionTimeHHmm(sess.endTime) ?? occ.endTime;
    return {
      ...occ,
      sessionId: sess.id,
      startTime: st,
      endTime: et,
      status: sess.status,
      sessionCoachIds: sess.coachIds,
      hasCoachOverride: sess.hasCoachOverride,
      coachOverrideCreatedAt: sess.coachOverrideCreatedAt,
      coachOverrideCreatedBy: sess.coachOverrideCreatedBy,
      ...courtFields,
    };
  });

  if (!mergeContext?.lessons?.length) return enriched;

  const lessonMap = new Map(mergeContext.lessons.map((l) => [l.id, l]));
  const termMap = new Map(mergeContext.terms.map((t) => [t.id, t]));
  const keys = new Set(enriched.map((o) => `${o.lessonId}:${format(o.date, 'yyyy-MM-dd')}`));

  const makeupCountByKey = new Map<string, number>();
  for (const m of mergeContext.makeupSessions ?? []) {
    const k = `${m.lessonId}:${m.date}`;
    makeupCountByKey.set(k, (makeupCountByKey.get(k) ?? 0) + 1);
  }
  const absenceCountByKey = new Map<string, number>();
  for (const r of mergeContext.reportedAbsenceSessions ?? []) {
    const k = `${r.lessonId}:${r.date}`;
    absenceCountByKey.set(k, (absenceCountByKey.get(k) ?? 0) + 1);
  }

  const orphans: ScheduleOccurrence[] = [];
  const orphanSeen = new Set<string>();

  for (const sess of lessonSessions) {
    const ymd = sess.sessionDate.replace(/T.*/, '').slice(0, 10);
    const key = `${sess.lessonId}:${ymd}`;
    if (keys.has(key)) continue;
    if (sess.status === 'postponed') continue;
    if (orphanSeen.has(key)) continue;
    const lesson = lessonMap.get(sess.lessonId);
    if (!lesson) continue;

    const occDate = parseLocalYmd(ymd);
    const makeupCount = makeupCountByKey.get(key) ?? 0;
    const reportedAbsenceCount = absenceCountByKey.get(key) ?? 0;
    const pendingEnrollmentCount =
      mergeContext.pendingScheduleBookings !== undefined
        ? countPendingForOccurrence(lesson.id, ymd, mergeContext.pendingScheduleBookings)
        : (lesson.pendingEnrollment ?? 0);

    const st = normalizeSessionTimeHHmm(sess.startTime) ?? lesson.startTime ?? '09:00';
    const et = normalizeSessionTimeHHmm(sess.endTime) ?? lesson.endTime ?? '10:00';
    const maxCap = lesson.maxCapacity ?? 0;
    const enrolled = resolveOccurrenceEnrollmentCount(
      lesson,
      ymd,
      mergeContext.scheduleEnrollmentBookings,
      mergeContext.removedFromSessionEnrollments ?? []
    );

    const courtFields = courtFieldsForOccurrence(lesson, sess, mergeContext?.courtNamesById);

    const common = {
      ...LESSON_OCCURRENCE_SOURCE,
      date: occDate,
      lessonId: lesson.id,
      lesson,
      lessonName: lesson.name,
      startTime: st,
      endTime: et,
      status: sess.status,
      ballColor: lesson.ballColor,
      ageGroup: lesson.ageGroup,
      skillLevel: lesson.skillLevel,
      enrollmentCount: enrolled,
      maxCapacity: maxCap,
      sessionId: sess.id,
      sessionCoachIds: sess.coachIds,
      hasCoachOverride: sess.hasCoachOverride,
      coachOverrideCreatedAt: sess.coachOverrideCreatedAt,
      coachOverrideCreatedBy: sess.coachOverrideCreatedBy,
      makeupCount: makeupCount > 0 ? makeupCount : undefined,
      pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
      reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
      ...courtFields,
    };

    if (lesson.lessonType === 'private') {
      orphans.push({ ...common, lessonType: 'private', ...privateOccurrenceFlags(lesson) });
    } else if (lesson.lessonType === 'camp') {
      orphans.push({ ...common, lessonType: 'camp', isCamp: true });
    } else if (lesson.lessonType === 'group') {
      const term = lesson.termId ? termMap.get(lesson.termId) : undefined;
      orphans.push({
        ...common,
        lessonType: 'group',
        termName: term?.name,
      });
    }

    orphanSeen.add(key);
  }

  return [...enriched, ...orphans].sort((a, b) => a.date.getTime() - b.date.getTime());
}

export type ScheduleActiveStatusFilter = 'all' | 'active' | 'inactive';

/** Whether a lesson should appear on owner/coach schedule calendars. */
export function isLessonIncludedOnAcademyCalendar(
  lesson: Lesson,
  termMap: Map<string, Term>,
  activeStatusFilter: ScheduleActiveStatusFilter = 'active'
): boolean {
  if (activeStatusFilter === 'inactive') {
    if (lesson.isActive === false) return true;
    return false;
  }
  if (activeStatusFilter === 'all') {
    return true;
  }
  if (lesson.isActive === false) return false;
  if (lesson.termId) {
    const term = termMap.get(lesson.termId);
    if (term?.isActive === false) return false;
  }
  return true;
}

/**
 * Build owner/coach calendar chips from materialized `lesson_sessions` only (authoritative schedule).
 * Lessons without synced session rows for the loaded range do not appear until sessions exist.
 */
export function buildAcademyOccurrencesFromLessonSessions(
  lessons: Lesson[],
  terms: Term[],
  lessonSessions: LessonSession[] | undefined | null,
  makeupSessions: MakeupSessionRef[] = [],
  pendingScheduleBookings?: SchedulePendingBookingRef[],
  reportedAbsenceSessions: ReportedAbsenceSessionRef[] = [],
  courtNamesById?: Record<string, string>,
  /** Coach schedule only: omit sessions whose resolved roster (session override or lesson default) excludes this coach. */
  scheduleForCoachId?: string,
  activeStatusFilter: ScheduleActiveStatusFilter = 'active',
  scheduleEnrollmentBookings?: ScheduleEnrollmentBookingRef[],
  removedFromSessionEnrollments?: RemovedFromSessionRef[]
): ScheduleOccurrence[] {
  if (!lessonSessions?.length) return [];

  const lessonMap = new Map(lessons.map((l) => [l.id, l]));
  const termMap = new Map(terms.map((t) => [t.id, t]));

  const makeupCountByKey = new Map<string, number>();
  for (const m of makeupSessions) {
    const k = `${m.lessonId}:${m.date}`;
    makeupCountByKey.set(k, (makeupCountByKey.get(k) ?? 0) + 1);
  }
  const absenceCountByKey = new Map<string, number>();
  for (const r of reportedAbsenceSessions) {
    const k = `${r.lessonId}:${r.date}`;
    absenceCountByKey.set(k, (absenceCountByKey.get(k) ?? 0) + 1);
  }

  const sortedSessions = [...lessonSessions].sort((a, b) => {
    const ay = a.sessionDate.replace(/T.*/, '').slice(0, 10);
    const by = b.sessionDate.replace(/T.*/, '').slice(0, 10);
    if (ay !== by) return ay.localeCompare(by);
    return (a.startTime ?? '').localeCompare(b.startTime ?? '');
  });

  const seen = new Set<string>();
  const out: ScheduleOccurrence[] = [];

  for (const sess of sortedSessions) {
    const ymd = sess.sessionDate.replace(/T.*/, '').slice(0, 10);
    const key = `${sess.lessonId}:${ymd}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const lesson = lessonMap.get(sess.lessonId);
    if (!lesson) continue;
    if (!isLessonIncludedOnAcademyCalendar(lesson, termMap, activeStatusFilter)) continue;

    if (scheduleForCoachId) {
      const roster = resolveSessionCoachIds(lesson, sess);
      if (!roster.includes(scheduleForCoachId)) continue;
    }

    const occDate = parseLocalYmd(ymd);
    const makeupCount = makeupCountByKey.get(key) ?? 0;
    const reportedAbsenceCount = absenceCountByKey.get(key) ?? 0;
    const pendingEnrollmentCount =
      pendingScheduleBookings !== undefined
        ? countPendingForOccurrence(lesson.id, ymd, pendingScheduleBookings)
        : (lesson.pendingEnrollment ?? 0);

    const st = normalizeSessionTimeHHmm(sess.startTime) ?? lesson.startTime ?? '09:00';
    const et = normalizeSessionTimeHHmm(sess.endTime) ?? lesson.endTime ?? '10:00';
    const maxCap = lesson.maxCapacity ?? 0;
    const enrolled = resolveOccurrenceEnrollmentCount(
      lesson,
      ymd,
      scheduleEnrollmentBookings,
      removedFromSessionEnrollments ?? []
    );
    const courtFields = courtFieldsForOccurrence(lesson, sess, courtNamesById);

    const common = {
      ...LESSON_OCCURRENCE_SOURCE,
      date: occDate,
      lessonId: lesson.id,
      lesson,
      lessonName: lesson.name,
      startTime: st,
      endTime: et,
      status: sess.status,
      ballColor: lesson.ballColor,
      ageGroup: lesson.ageGroup,
      skillLevel: lesson.skillLevel,
      enrollmentCount: enrolled,
      maxCapacity: maxCap,
      sessionId: sess.id,
      sessionCoachIds: sess.coachIds,
      hasCoachOverride: sess.hasCoachOverride,
      coachOverrideCreatedAt: sess.coachOverrideCreatedAt,
      coachOverrideCreatedBy: sess.coachOverrideCreatedBy,
      makeupCount: makeupCount > 0 ? makeupCount : undefined,
      pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
      reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
      ...courtFields,
    };

    if (lesson.lessonType === 'private') {
      out.push({ ...common, lessonType: 'private', ...privateOccurrenceFlags(lesson) });
    } else if (lesson.lessonType === 'camp') {
      out.push({ ...common, lessonType: 'camp', isCamp: true });
    } else if (lesson.lessonType === 'group') {
      const term = lesson.termId ? termMap.get(lesson.termId) : undefined;
      out.push({
        ...common,
        lessonType: 'group',
        termName: term?.name,
      });
    }
  }

  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

function toDate(
  value: Date | string | { toDate: () => Date } | undefined
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') return new Date(value);
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  try {
    return new Date(value as unknown as string);
  } catch {
    return null;
  }
}

/**
 * Build schedule occurrences for academy owner and coach schedule views.
 * Handles term lessons (via generateLessonOccurrences) and private lessons (startDate + numberOfSessions + optional daysOfWeek).
 * Maps overrides to cancelled/postponed status. Returns sorted by date.
 *
 * @param lessons - Lessons to build occurrences for
 * @param terms - Terms (used for term-based lessons)
 * @param overrides - Lesson overrides for cancellations/postponements
 * @param makeupSessions - Optional (lessonId, date) pairs where a make-up credit was used for that session
 * @param pendingScheduleBookings - When set, per-occurrence pending counts; when omitted, uses lesson.pendingEnrollment (lesson-wide)
 * @param reportedAbsenceSessions - Optional per-booking absence refs for this session (badges)
 * @returns Schedule occurrences sorted by date
 */
export function buildAcademyOccurrences(
  lessons: Lesson[],
  terms: Term[],
  overrides: LessonOverride[] = [],
  makeupSessions: MakeupSessionRef[] = [],
  pendingScheduleBookings?: SchedulePendingBookingRef[],
  reportedAbsenceSessions: ReportedAbsenceSessionRef[] = [],
  courtNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  const occurrences: ScheduleOccurrence[] = [];
  const termMap = new Map(terms.map((t) => [t.id, t]));

  const makeupCountByKey = new Map<string, number>();
  for (const m of makeupSessions) {
    const key = `${m.lessonId}:${m.date}`;
    makeupCountByKey.set(key, (makeupCountByKey.get(key) ?? 0) + 1);
  }

  const absenceCountByKey = new Map<string, number>();
  for (const r of reportedAbsenceSessions) {
    const key = `${r.lessonId}:${r.date}`;
    absenceCountByKey.set(key, (absenceCountByKey.get(key) ?? 0) + 1);
  }

  for (const lesson of lessons) {
    if (!isLessonIncludedOnAcademyCalendar(lesson, termMap)) continue;

    const maxCap = lesson.maxCapacity ?? 0;
    const enrolled = lesson.currentEnrollment ?? 0;
    const courtLabel = courtNamesLabelFromLesson(lesson, courtNamesById);

    // Term-bound private 1-on-1: same expansion as group lessons within the term
    if (lesson.lessonType === 'private' && lesson.termId) {
      const term = termMap.get(lesson.termId);
      if (!term || !lesson.daysOfWeek?.length) continue;
      const lessonOverrides = overrides.filter((o) => o.lessonId === lesson.id);
      const occs = generateLessonOccurrences(
        {
          id: lesson.id,
          daysOfWeek: lesson.daysOfWeek,
          startTime: lesson.startTime ?? '09:00',
          endTime: lesson.endTime ?? '10:00',
        },
        {
          startDate: toDate(term.startDate) ?? new Date(term.startDate),
          endDate: toDate(term.endDate) ?? new Date(term.endDate),
        },
        lessonOverrides
      );
      for (const occ of occs) {
        const dateKey = format(occ.date, 'yyyy-MM-dd');
        const makeupCount = makeupCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const reportedAbsenceCount = absenceCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const pendingEnrollmentCount =
          pendingScheduleBookings !== undefined
            ? countPendingForOccurrence(lesson.id, dateKey, pendingScheduleBookings)
            : lesson.pendingEnrollment ?? 0;
        occurrences.push({
          ...LESSON_OCCURRENCE_SOURCE,
          date: occ.date,
          lessonId: lesson.id,
          lesson,
          lessonName: lesson.name,
          lessonType: 'private',
          startTime: occ.startTime,
          endTime: occ.endTime,
          status: occ.status,
          ballColor: lesson.ballColor,
          ageGroup: lesson.ageGroup,
          skillLevel: lesson.skillLevel,
          enrollmentCount: enrolled,
          maxCapacity: maxCap,
          ...privateOccurrenceFlags(lesson),
          makeupCount: makeupCount > 0 ? makeupCount : undefined,
          pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
          reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
          courtLabel,
        });
      }
      continue;
    }

    // Standalone private lessons: startDate + numberOfSessions (optional multi-day via daysOfWeek)
    if (
      lesson.lessonType === 'private' &&
      lesson.startDate &&
      lesson.numberOfSessions
    ) {
      const n = lesson.numberOfSessions;
      const lessonOverrides = overrides.filter((o) => o.lessonId === lesson.id);

      const sessionDates = generatePrivateLessonOccurrenceDates(
        lesson.startDate,
        n,
        lesson.daysOfWeek
      );

      for (const occDate of sessionDates) {
        const override = lessonOverrides.find((o) => {
          const orig = toDate(o.originalDate as Date | string);
          return (
            orig &&
            format(orig, 'yyyy-MM-dd') === format(occDate, 'yyyy-MM-dd')
          );
        });

        const status = override
          ? override.overrideType === 'cancelled'
            ? 'cancelled'
            : 'postponed'
          : 'scheduled';

        const dateKey = format(occDate, 'yyyy-MM-dd');
        const makeupCount = makeupCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const reportedAbsenceCount = absenceCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const pendingEnrollmentCount =
          pendingScheduleBookings !== undefined
            ? countPendingForOccurrence(lesson.id, dateKey, pendingScheduleBookings)
            : lesson.pendingEnrollment ?? 0;

        occurrences.push({
          ...LESSON_OCCURRENCE_SOURCE,
          date: occDate,
          lessonId: lesson.id,
          lesson,
          lessonName: lesson.name,
          lessonType: 'private',
          startTime: lesson.startTime ?? '09:00',
          endTime: lesson.endTime ?? '10:00',
          status,
          ballColor: lesson.ballColor,
          ageGroup: lesson.ageGroup,
          skillLevel: lesson.skillLevel,
          enrollmentCount: enrolled,
          maxCapacity: maxCap,
          ...privateOccurrenceFlags(lesson),
          makeupCount: makeupCount > 0 ? makeupCount : undefined,
          pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
          reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
          courtLabel,
        });
      }
      continue;
    }

    // Standalone camps (date range + daysOfWeek)
    if (lesson.lessonType === 'camp') {
      if (!lesson.startDate || !lesson.endDate || !lesson.daysOfWeek?.length) continue;
      const start = toDate(lesson.startDate);
      const end = toDate(lesson.endDate);
      if (!start || !end) continue;
      const lessonOverrides = overrides.filter((o) => o.lessonId === lesson.id);
      const occs = generateLessonOccurrences(
        {
          id: lesson.id,
          daysOfWeek: lesson.daysOfWeek,
          startTime: lesson.startTime ?? '09:00',
          endTime: lesson.endTime ?? '10:00',
        },
        { startDate: start, endDate: end },
        lessonOverrides
      );
      for (const occ of occs) {
        const dateKey = format(occ.date, 'yyyy-MM-dd');
        const makeupCount = makeupCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const reportedAbsenceCount = absenceCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
        const pendingEnrollmentCount =
          pendingScheduleBookings !== undefined
            ? countPendingForOccurrence(lesson.id, dateKey, pendingScheduleBookings)
            : lesson.pendingEnrollment ?? 0;
        occurrences.push({
          ...LESSON_OCCURRENCE_SOURCE,
          date: occ.date,
          lessonId: lesson.id,
          lesson,
          lessonName: lesson.name,
          lessonType: 'camp',
          startTime: occ.startTime,
          endTime: occ.endTime,
          status: occ.status,
          ballColor: lesson.ballColor,
          ageGroup: lesson.ageGroup,
          skillLevel: lesson.skillLevel,
          enrollmentCount: enrolled,
          maxCapacity: maxCap,
          isCamp: true,
          makeupCount: makeupCount > 0 ? makeupCount : undefined,
          pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
          reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
          courtLabel,
        });
      }
      continue;
    }

    // Term-bound group lessons
    if (lesson.lessonType !== 'group') continue;
    const term = lesson.termId ? termMap.get(lesson.termId) : null;
    if (!term || !lesson.daysOfWeek?.length) continue;

    const lessonOverrides = overrides.filter((o) => o.lessonId === lesson.id);

    const occs = generateLessonOccurrences(
      {
        id: lesson.id,
        daysOfWeek: lesson.daysOfWeek,
        startTime: lesson.startTime ?? '09:00',
        endTime: lesson.endTime ?? '10:00',
      },
      {
        startDate: toDate(term.startDate) ?? new Date(term.startDate),
        endDate: toDate(term.endDate) ?? new Date(term.endDate),
      },
      lessonOverrides
    );

    for (const occ of occs) {
      const dateKey = format(occ.date, 'yyyy-MM-dd');
      const makeupCount = makeupCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
      const reportedAbsenceCount = absenceCountByKey.get(`${lesson.id}:${dateKey}`) ?? 0;
      const pendingEnrollmentCount =
        pendingScheduleBookings !== undefined
          ? countPendingForOccurrence(lesson.id, dateKey, pendingScheduleBookings)
          : lesson.pendingEnrollment ?? 0;
      occurrences.push({
        ...LESSON_OCCURRENCE_SOURCE,
        date: occ.date,
        lessonId: lesson.id,
        lesson,
        lessonName: lesson.name,
        lessonType: 'group',
        startTime: occ.startTime,
        endTime: occ.endTime,
        status: occ.status,
        ballColor: lesson.ballColor,
        ageGroup: lesson.ageGroup,
        skillLevel: lesson.skillLevel,
        enrollmentCount: enrolled,
        maxCapacity: maxCap,
        termName: term.name,
        makeupCount: makeupCount > 0 ? makeupCount : undefined,
        pendingEnrollmentCount: pendingEnrollmentCount > 0 ? pendingEnrollmentCount : undefined,
        reportedAbsenceCount: reportedAbsenceCount > 0 ? reportedAbsenceCount : undefined,
        courtLabel,
      });
    }
  }

  return occurrences.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/** Snapshot of a `lesson_sessions` row for linked postpone / reschedule UI */
export type LessonSessionLinkSummary = {
  id: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'cancelled' | 'postponed';
  overrideReason?: string;
};

/** Payload when user selects a lesson occurrence in owner/coach calendar */
export interface LessonOccurrenceClickPayload {
  lesson: Lesson;
  sessionDateYmd: string;
  date: Date;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'cancelled' | 'postponed';
  lessonName: string;
  lessonType: 'group' | 'private' | 'camp';
  isPrivate?: boolean;
  sessionId?: string;
  /** Resolved coach roster for this occurrence (from materialized session when available) */
  sessionCoachIds?: string[];
  hasCoachOverride?: boolean;
  coachOverrideCreatedAt?: Date;
  coachOverrideCreatedBy?: string;
  /** Students attending via make-up credit (from schedule make-up refs). */
  makeupBookingCount?: number;
  /** When this occurrence is postponed, the replacement session row */
  replacementSession?: LessonSessionLinkSummary;
  /** When this occurrence is the replacement, the original postponed session row */
  originalSession?: LessonSessionLinkSummary;
  /** Reason on the materialized session row (e.g. postpone/cancel note) */
  materializedSessionOverrideReason?: string;
}

function lessonSessionToLinkSummary(s: LessonSession): LessonSessionLinkSummary {
  return {
    id: s.id,
    sessionDate: s.sessionDate.replace(/T.*/, '').slice(0, 10),
    startTime: normalizeSessionTimeHHmm(s.startTime) ?? s.startTime,
    endTime: normalizeSessionTimeHHmm(s.endTime) ?? s.endTime,
    status: s.status,
    overrideReason: s.overrideReason,
  };
}

function findLessonSessionForPayload(
  base: LessonOccurrenceClickPayload,
  lessonSessions: LessonSession[]
): LessonSession | undefined {
  if (base.sessionId) {
    const byId = lessonSessions.find((s) => s.id === base.sessionId);
    if (byId) return byId;
  }
  return lessonSessions.find(
    (s) =>
      s.lessonId === base.lesson.id &&
      s.sessionDate.replace(/T.*/, '').slice(0, 10) === base.sessionDateYmd
  );
}

export function scheduleOccurrenceToClickPayload(occ: ScheduleOccurrence): LessonOccurrenceClickPayload {
  return {
    lesson: occ.lesson,
    sessionDateYmd: format(occ.date, 'yyyy-MM-dd'),
    date: occ.date,
    startTime: occ.startTime,
    endTime: occ.endTime,
    status: occ.status,
    lessonName: occ.lessonName,
    lessonType: occ.lessonType,
    isPrivate: occ.isPrivate,
    sessionId: occ.sessionId,
    sessionCoachIds: occ.sessionCoachIds,
    hasCoachOverride: occ.hasCoachOverride,
    coachOverrideCreatedAt: occ.coachOverrideCreatedAt,
    coachOverrideCreatedBy: occ.coachOverrideCreatedBy,
    makeupBookingCount: occ.makeupCount,
  };
}

/** Monthly calendar uses generated occurrences; merge materialized session id/times when available. */
export function enrichLessonCalendarClickPayloadWithSessions(
  base: LessonOccurrenceClickPayload,
  lessonSessions: LessonSession[] | undefined
): LessonOccurrenceClickPayload {
  if (!lessonSessions?.length) return base;
  const sess = findLessonSessionForPayload(base, lessonSessions);
  if (!sess) return base;
  return {
    ...base,
    sessionId: sess.id,
    status: sess.status,
    startTime: normalizeSessionTimeHHmm(sess.startTime) ?? base.startTime,
    endTime: normalizeSessionTimeHHmm(sess.endTime) ?? base.endTime,
    sessionCoachIds: sess.coachIds,
    hasCoachOverride: sess.hasCoachOverride,
    coachOverrideCreatedAt: sess.coachOverrideCreatedAt,
    coachOverrideCreatedBy: sess.coachOverrideCreatedBy,
  };
}

/** Links postponed rows to replacement sessions and replacements back to originals (lesson_sessions). */
export function enrichLessonOccurrenceClickPayloadWithSessionRelations(
  base: LessonOccurrenceClickPayload,
  lessonSessions: LessonSession[] | undefined
): LessonOccurrenceClickPayload {
  if (!lessonSessions?.length) return base;
  const sess = findLessonSessionForPayload(base, lessonSessions);
  if (!sess) return base;

  let replacementSession: LessonSessionLinkSummary | undefined;
  let originalSession: LessonSessionLinkSummary | undefined;

  if (sess.status === 'postponed' && sess.newSessionId) {
    const rep = lessonSessions.find((s) => s.id === sess.newSessionId);
    if (rep) replacementSession = lessonSessionToLinkSummary(rep);
  }
  const orig = lessonSessions.find(
    (s) => s.newSessionId === sess.id && s.status === 'postponed'
  );
  if (orig) originalSession = lessonSessionToLinkSummary(orig);

  return {
    ...base,
    replacementSession,
    originalSession,
    materializedSessionOverrideReason: sess.overrideReason,
  };
}

/** Session times + postpone/make-up links in one step (weekly + monthly calendar clicks). */
export function enrichLessonOccurrenceClickPayloadFully(
  base: LessonOccurrenceClickPayload,
  lessonSessions: LessonSession[] | undefined
): LessonOccurrenceClickPayload {
  const withTimes = enrichLessonCalendarClickPayloadWithSessions(base, lessonSessions);
  return enrichLessonOccurrenceClickPayloadWithSessionRelations(withTimes, lessonSessions);
}

export function lessonCalendarOccurrenceToClickPayload(occ: {
  lesson: Lesson;
  date: Date;
  status: 'scheduled' | 'cancelled' | 'postponed';
  sessionId?: string;
  makeupCount?: number;
}): LessonOccurrenceClickPayload {
  const { lesson, date, status, sessionId, makeupCount } = occ;
  return {
    lesson,
    sessionDateYmd: format(date, 'yyyy-MM-dd'),
    date,
    startTime: lesson.startTime ?? '09:00',
    endTime: lesson.endTime ?? '10:00',
    status,
    lessonName: lesson.name,
    lessonType: lesson.lessonType,
    isPrivate: lesson.lessonType === 'private',
    sessionId,
    makeupBookingCount: makeupCount,
  };
}

/** Synthetic bucket key for sessions with no court assignment. */
export const UNASSIGNED_COURT_BUCKET = '__unassigned__' as const;

/** Synthetic bucket key for unassigned coaches and non-lesson facility items in coach day view. */
export const UNASSIGNED_COACH_BUCKET = '__unassigned_coach__' as const;

/** Resolved coach roster for a materialized schedule occurrence. */
export function resolveOccurrenceCoachIds(occ: ScheduleOccurrence): string[] {
  return resolveSessionCoachIds(occ.lesson, {
    coachIds: occ.sessionCoachIds,
  });
}

/**
 * Bucket one day's schedule occurrences by coach column.
 * Lessons with multiple coaches appear in each coach column.
 * Non-lesson types (court hire, closures, play events) go to Unassigned.
 */
export function groupOccurrencesByCoach(
  occurrences: ScheduleOccurrence[],
  coachIds: string[]
): Map<string, ScheduleOccurrence[]> {
  const map = new Map<string, ScheduleOccurrence[]>();
  map.set(UNASSIGNED_COACH_BUCKET, []);
  for (const coachId of coachIds) {
    map.set(coachId, []);
  }
  for (const occ of occurrences) {
    if (occ.sourceType !== 'lesson') {
      map.get(UNASSIGNED_COACH_BUCKET)!.push(occ);
      continue;
    }
    const ids = resolveOccurrenceCoachIds(occ);
    if (ids.length === 0) {
      map.get(UNASSIGNED_COACH_BUCKET)!.push(occ);
    } else {
      for (const coachId of ids) {
        if (!map.has(coachId)) {
          map.set(coachId, []);
        }
        map.get(coachId)!.push(occ);
      }
    }
  }
  return map;
}

function stubLessonForSchedule(id: string, name: string, academyId = ''): Lesson {
  return {
    id,
    academyId,
    name,
    lessonType: 'group',
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
  } as Lesson;
}

function isoToLocalDateAndTimes(startAt: string, endAt: string): {
  date: Date;
  startTime: string;
  endTime: string;
} {
  const start = new Date(startAt);
  const end = new Date(endAt);
  const ymd = format(start, 'yyyy-MM-dd');
  return {
    date: parseLocalYmd(ymd),
    startTime: format(start, 'HH:mm'),
    endTime: format(end, 'HH:mm'),
  };
}

/** Map unified occupancy API rows to lesson-session schedule occurrences. */
export function buildLessonOccurrencesFromOccupancy(
  items: UnifiedOccupancyCalendarItem[],
  courtNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  return items
    .filter(
      (item) =>
        (item.type === 'lesson_session' || item.sourceType === 'lesson_session') &&
        item.sourceType !== 'play_event'
    )
    .map((item) => {
      const { date, startTime, endTime } = isoToLocalDateAndTimes(item.startAt, item.endAt);
      const lessonName = item.lessonName?.trim() || 'Lesson';
      const lessonId = item.lessonId ?? item.sourceId ?? item.id;
      const resolvedCourtIds =
        item.resourceType === 'court' ? [item.resourceId] : [];
      return {
        sourceType: 'lesson',
        date,
        lessonId,
        lesson: stubLessonForSchedule(lessonId, lessonName),
        lessonName,
        lessonType: 'group',
        startTime,
        endTime,
        status: 'scheduled',
        enrollmentCount: 0,
        maxCapacity: 0,
        resolvedCourtIds,
        courtLabel: courtNamesLabelFromCourtIds(resolvedCourtIds, courtNamesById),
      };
    });
}

/** Map closure occupancy rows to schedule occurrences (read-only styling on club calendar). */
export function buildClosureOccurrencesFromOccupancy(
  items: UnifiedOccupancyCalendarItem[],
  courtNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  return items
    .filter((item) => item.type === 'closure' || item.sourceType === 'closure')
    .map((item) => {
      const { date, startTime, endTime } = isoToLocalDateAndTimes(item.startAt, item.endAt);
      const resolvedCourtIds =
        item.resourceType === 'court' ? [item.resourceId] : [];
      return {
        sourceType: 'closure',
        date,
        lessonId: item.id,
        lesson: stubLessonForSchedule(item.id, 'Closure'),
        lessonName: 'Closure',
        lessonType: 'group',
        startTime,
        endTime,
        status: 'scheduled',
        enrollmentCount: 0,
        maxCapacity: 0,
        resolvedCourtIds,
        courtLabel: courtNamesLabelFromCourtIds(resolvedCourtIds, courtNamesById),
      };
    });
}

/** Map court hire bookings to schedule occurrences for the club day view. */
export function buildCourtHireOccurrencesFromBookings(
  bookings: CourtHireBooking[],
  courtNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  return bookings
    .filter((b) => b.status !== 'cancelled')
    .map((booking) => {
      const startAt =
        booking.startAt instanceof Date ? booking.startAt.toISOString() : String(booking.startAt);
      const endAt =
        booking.endAt instanceof Date ? booking.endAt.toISOString() : String(booking.endAt);
      const { date, startTime, endTime } = isoToLocalDateAndTimes(startAt, endAt);
      const guestLabel = [booking.guestFirstName, booking.guestLastName].filter(Boolean).join(' ');
      const lessonName = guestLabel ? `Court hire — ${guestLabel}` : 'Court hire';
      return {
        sourceType: 'court_hire',
        date,
        lessonId: booking.id,
        lesson: stubLessonForSchedule(booking.id, lessonName),
        lessonName,
        lessonType: 'group',
        startTime,
        endTime,
        status: booking.status === 'confirmed' ? 'scheduled' : 'scheduled',
        enrollmentCount: 0,
        maxCapacity: 0,
        resolvedCourtIds: [booking.courtId],
        courtLabel: courtNamesLabelFromCourtIds([booking.courtId], courtNamesById),
      };
    });
}

/**
 * Bucket one day's schedule occurrences by court column (multi-court lessons appear in each column).
 */
export function groupOccurrencesByCourt(
  occurrences: ScheduleOccurrence[],
  courtIds: string[]
): Map<string, ScheduleOccurrence[]> {
  const map = new Map<string, ScheduleOccurrence[]>();
  map.set(UNASSIGNED_COURT_BUCKET, []);
  for (const courtId of courtIds) {
    map.set(courtId, []);
  }
  for (const occ of occurrences) {
    const ids = occ.resolvedCourtIds ?? [];
    if (ids.length === 0) {
      map.get(UNASSIGNED_COURT_BUCKET)!.push(occ);
    } else {
      for (const courtId of ids) {
        if (!map.has(courtId)) {
          map.set(courtId, []);
        }
        map.get(courtId)!.push(occ);
      }
    }
  }
  return map;
}

export function isScheduleOccurrenceInDateRange(
  occurrence: ScheduleOccurrence,
  rangeStart: Date,
  rangeEnd: Date
): boolean {
  const dayMs = startOfDay(occurrence.date).getTime();
  return (
    dayMs >= startOfDay(rangeStart).getTime() && dayMs <= startOfDay(rangeEnd).getTime()
  );
}

/** Map play events to schedule occurrences for the unified facility calendar. */
export function buildPlayEventOccurrencesFromEvents(
  events: PlayEvent[],
  courtNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  return events
    .filter((event) => event.status !== 'draft')
    .map((event) => {
      const startAt =
        event.startsAt instanceof Date ? event.startsAt.toISOString() : String(event.startsAt);
      const endAt = event.endsAt instanceof Date ? event.endsAt.toISOString() : String(event.endsAt);
      const { date, startTime, endTime } = isoToLocalDateAndTimes(startAt, endAt);
      const occurrenceStatus =
        event.status === 'cancelled' ? ('cancelled' as const) : ('scheduled' as const);
      return {
        sourceType: 'play_event' as const,
        date,
        lessonId: event.id,
        lesson: stubLessonForSchedule(event.id, event.name),
        lessonName: event.name,
        lessonType: 'group' as const,
        startTime,
        endTime,
        status: occurrenceStatus,
        enrollmentCount: 0,
        maxCapacity: event.capacity ?? 0,
        resolvedCourtIds: event.courtIds,
        courtLabel: courtNamesLabelFromCourtIds(event.courtIds, courtNamesById),
      };
    });
}

type MutablePlayEventOccurrence = ScheduleOccurrence & { courtIdSet: Set<string> };

/** Supplement play events from court occupancy rows (series sessions with court holds). */
export function buildPlayEventOccurrencesFromOccupancy(
  items: UnifiedOccupancyCalendarItem[],
  courtNamesById?: Record<string, string>,
  eventNamesById?: Record<string, string>
): ScheduleOccurrence[] {
  const byEventId = new Map<string, MutablePlayEventOccurrence>();

  for (const item of items) {
    if (item.sourceType !== 'play_event') continue;
    const eventId = item.sourceId ?? item.id;
    const { date, startTime, endTime } = isoToLocalDateAndTimes(item.startAt, item.endAt);
    const name = eventNamesById?.[eventId]?.trim() || item.lessonName?.trim() || 'Play event';

    let occ = byEventId.get(eventId);
    if (!occ) {
      occ = {
        sourceType: 'play_event',
        date,
        lessonId: eventId,
        lesson: stubLessonForSchedule(eventId, name),
        lessonName: name,
        lessonType: 'group',
        startTime,
        endTime,
        status: 'scheduled',
        enrollmentCount: 0,
        maxCapacity: 0,
        resolvedCourtIds: [],
        courtLabel: '',
        courtIdSet: new Set<string>(),
      };
      byEventId.set(eventId, occ);
    }
    if (item.resourceType === 'court') {
      occ.courtIdSet.add(item.resourceId);
    }
  }

  return [...byEventId.values()].map((occ) => {
    const courtIds = [...occ.courtIdSet];
    return {
      sourceType: occ.sourceType,
      date: occ.date,
      lessonId: occ.lessonId,
      lesson: occ.lesson,
      lessonName: occ.lessonName,
      lessonType: occ.lessonType,
      startTime: occ.startTime,
      endTime: occ.endTime,
      status: occ.status,
      enrollmentCount: occ.enrollmentCount,
      maxCapacity: occ.maxCapacity,
      resolvedCourtIds: courtIds,
      courtLabel: courtNamesLabelFromCourtIds(courtIds, courtNamesById),
    };
  });
}

export function mergePlayEventOccurrences(
  primary: ScheduleOccurrence[],
  supplemental: ScheduleOccurrence[]
): ScheduleOccurrence[] {
  const byId = new Map(primary.map((occ) => [occ.lessonId, occ]));
  for (const occ of supplemental) {
    if (!byId.has(occ.lessonId)) {
      byId.set(occ.lessonId, occ);
    }
  }
  return [...byId.values()];
}
